import { mapGetters, mapActions } from 'vuex'
import { FETCH_PRODUCT_FILTER_TABS_MENU } from '~/store/mutation-types'

const orderTargets = new Map()
const orderWorkers = new Map()
const orderLastSent = new Map()

export default {
  computed: {
    ...mapGetters({
      isAdmin: 'auth/isAdmin',
      activeDraftOrder: 'orders/activeDraftOrder'
    })
  },
  methods: {
    ...mapActions({
      addProduct: 'orders/addProduct',
      fetchCatalogProducts: 'catalog_products/fetchCatalogProducts',
      setAddEditOrderModelWithCreateActiveOrderProduct: 'add_edit_order_modal/setAddEditOrderModelWithCreateActiveOrderProduct',
      setNewOrderMode: 'orders/setNewOrderMode',
      setNewOrderProduct: 'orders/setNewOrderProduct',
      removeProduct: 'orders/removeProduct',
      fetchOrders: 'orders/fetchOrders',
      toggleCatalogProductsProperty: 'catalog_products/toggleCatalogProductsProperty',
      setCatalogProductsSelectedSortBy: 'catalog_products/setSelectedSortBy',
      setProductActiveOrder: 'catalog_products/setProductActiveOrder'
    }),

    async doOrder ({ index, property, product, productOrder }) {
      if (property !== 'active_order') {
        return
      }

      const combinedProductAndOrderedProduct = Object.assign({}, product, productOrder)
      if (!this.activeDraftOrder) {
        await this.setAddEditOrderModelWithCreateActiveOrderProduct(true)
        await this.setNewOrderMode('addOneProduct')
        await this.setNewOrderProduct(combinedProductAndOrderedProduct)
        this.$modal.show('new-order-modal')
        return
      }

      const productId = product.id
      const desired = !product.active_order

      this.setProductActiveOrder({ productId, value: desired })
      orderTargets.set(productId, desired)

      if (orderWorkers.has(productId)) {
        // A toggle for this product is already running; awaiting it resolves once the
        // whole burst (including the trailing fetchOrders) settles.
        return orderWorkers.get(productId)
      }

      const ctx = {
        orderId: this.activeDraftOrder.id,
        productOrder,
        addProduct: this.addProduct,
        removeProduct: this.removeProduct,
        fetchOrders: this.fetchOrders,
        setProductActiveOrder: this.setProductActiveOrder
      }

      const worker = this.runOrderToggleWorker(productId, ctx)
      orderWorkers.set(productId, worker)
      worker.finally(() => {
        if (orderWorkers.get(productId) === worker) {
          orderWorkers.delete(productId)
        }
      })
      // Returned so callers that need to act after the order is persisted (e.g. the header
      // search card refreshing/redirecting the checkout flow) can await it. Grid/detail
      // callers ignore the return, so this is backward-compatible.
      return worker
    },

    async runOrderToggleWorker (productId, ctx) {
      let lastSent = orderLastSent.get(productId)
      let needsFetch = false

      while (orderTargets.has(productId)) {
        const want = orderTargets.get(productId)
        if (want === lastSent) {
          orderTargets.delete(productId)
          break
        }

        try {
          if (want) {
            await ctx.addProduct({ productOrder: ctx.productOrder, productId })
          } else {
            await ctx.removeProduct({ orderId: ctx.orderId, productId })
          }
          lastSent = want
          orderLastSent.set(productId, want)
          needsFetch = true
        } catch (error) {
          const status = error?.response?.status
          if (status === 406) {
            lastSent = want
            orderLastSent.set(productId, want)
            needsFetch = true
          } else {
            ctx.setProductActiveOrder({ productId, value: !want })
            orderTargets.delete(productId)
            break
          }
        }
      }

      if (needsFetch) {
        try {
          await ctx.fetchOrders()
        } catch (e) {
          // ignore — UI state remains optimistic; next user action will reconcile
        }
      }
    },

    initiateCurrentlySelectedOrderData () {
      this.$store.dispatch('orders/fetchOrders')

      const routerName = this.$route.name

      this.setCatalogProductsSelectedSortBy({
        selectedSortBy: {
          key: 'brand',
          label: 'Brand'
        }
      })

      if (routerName === 'tag-module') {
        this.$store.commit('menus/' + FETCH_PRODUCT_FILTER_TABS_MENU)

        if (this.isAdmin) {
          this.fetchCatalogProducts()
        } else {
          this.fetchCatalogProducts({ urlEndpoint: '/api/product?selected_only_on_customer=1' })
        }

        return
      }

      if (routerName === 'collections') {
        this.$store.commit('menus/' + FETCH_PRODUCT_FILTER_TABS_MENU)
        this.fetchCatalogProducts({ urlEndpoint: `/api/product?collection_id=${this.selectedMyCollectionId}`, isCollectionsPage: true })

        return
      }

      if (routerName === 'checkout-proceed.order-summary') {
        this.$store.dispatch('checkout/fetchProductItemsOrderSummary')
      }
    }
  }
}
